import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2, Paperclip, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputClass = "w-full rounded-xl border border-brand/20 bg-white px-4 py-3.5 text-sm text-forest placeholder:text-brand/40 outline-none transition-all duration-300 focus:border-gold focus:ring-4 focus:ring-gold/10";
const labelClass = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-brand";

const ApplicationForm = ({ jobs, preset }) => {
  const { t, lang } = useLanguage();
  const r = t.recruit;
  const f = r.form;
  const empty = { name: "", phone: "", email: "", type: "job", job_id: "", domain: "", message: "" };
  const [form, setForm] = useState(empty);
  const [cv, setCv] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => { if (preset) setForm((s) => ({ ...s, type: preset.type, job_id: preset.id, domain: preset.domain || s.domain })); }, [preset]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries({ ...form, lang }).forEach(([k, v]) => fd.append(k, v));
      if (cv) fd.append("cv", cv);
      await axios.post(`${API}/applications`, fd);
      toast.success(f.success);
      setSent(true); setForm(empty); setCv(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || f.error);
    } finally { setSending(false); }
  };

  if (sent) {
    return (
      <div className="flex flex-col items-start gap-6 rounded-3xl border border-brand/10 bg-white p-8 shadow-xl shadow-brand/5 sm:p-12" data-testid="application-sent-panel">
        <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-mint text-brand"><CheckCircle2 className="h-8 w-8" /></span>
        <h3 className="font-display text-2xl font-bold text-forest sm:text-3xl">{f.success}</h3>
        <button onClick={() => setSent(false)} data-testid="application-again-button" className="inline-flex items-center gap-2 rounded-full border-2 border-gold px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-gold transition-all hover:bg-gold hover:text-white">{f.again}</button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5 rounded-3xl border border-brand/10 bg-white p-6 shadow-xl shadow-brand/5 sm:p-10" data-testid="application-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <div><label className={labelClass}>{t.contact.form.name} *</label><input required value={form.name} onChange={set("name")} placeholder={t.contact.form.namePh} data-testid="application-name-input" className={inputClass} /></div>
        <div><label className={labelClass}>{t.contact.form.phoneSimple} *</label><input required value={form.phone} onChange={set("phone")} placeholder={t.contact.form.phonePh} data-testid="application-phone-input" className={inputClass} /></div>
      </div>
      <div><label className={labelClass}>{t.contact.form.emailSimple}</label><input type="email" value={form.email} onChange={set("email")} placeholder={t.contact.form.emailPh} data-testid="application-email-input" className={inputClass} /></div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelClass}>{f.type} *</label>
          <select value={form.type} onChange={(e) => setForm((s) => ({ ...s, type: e.target.value, job_id: "" }))} data-testid="application-type-select" className={inputClass}>
            {Object.entries(r.types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>{f.offer}</label>
          <select value={form.job_id} onChange={set("job_id")} data-testid="application-job-select" className={inputClass}>
            <option value="">{f.spontaneous}</option>
            {jobs.filter((j) => j.type === form.type).map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className={labelClass}>{f.domain}</label>
        <select value={form.domain} onChange={set("domain")} data-testid="application-domain-select" className={inputClass}>
          <option value="">{f.domainPh}</option>
          {f.domains.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
      <div><label className={labelClass}>{f.message}</label><textarea rows={4} value={form.message} onChange={set("message")} placeholder={f.messagePh} data-testid="application-message-input" className={`${inputClass} resize-none`} /></div>
      <div>
        <label className={labelClass}>{f.cv}</label>
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="application-cv-button" className="inline-flex items-center gap-2 rounded-full border border-brand/30 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-brand transition-colors hover:bg-mint"><Paperclip className="h-4 w-4" />{f.cvChoose}</button>
          <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,image/*" onChange={(e) => setCv(e.target.files?.[0] || null)} data-testid="application-cv-input" className="hidden" />
          {cv ? <span className="inline-flex items-center gap-2 rounded-full bg-mint px-3 py-1.5 text-xs text-forest" data-testid="application-cv-name">{cv.name}<button type="button" onClick={() => { setCv(null); if (fileRef.current) fileRef.current.value = ""; }} data-testid="application-cv-remove"><X className="h-3.5 w-3.5" /></button></span> : <span className="text-xs text-brand-hover">{f.cvOptional}</span>}
        </div>
      </div>
      <button type="submit" disabled={sending} data-testid="application-submit-button" className="group flex w-full items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-white shadow-xl shadow-gold/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover disabled:opacity-60 sm:w-auto">
        {sending ? f.sending : f.submit}<Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </form>
  );
};

export default ApplicationForm;
